import React from 'react';

import {
  Box,
  Text,
  VStack,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  Button,
  Badge,
  SimpleGrid,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Divider,
  Icon,
  Alert,
  AlertIcon,
} from '@chakra-ui/react';
import { FaSearch, FaBox, FaStore, FaTruck } from 'react-icons/fa';

import { formatDateTime } from 'utils/date.util';

import { useColors } from './colors';

const optimizerStatusColor = (status) => {
  if (status === 'optimized') return 'green';
  if (status === 'halted') return 'purple';
  if (status === 'partial') return 'yellow';
  if (status === 'error') return 'red';
  return 'orange';
};

const optimizerStatusLabel = (status) => {
  if (status === 'halted') return 'halted (max runs reached)';
  return status;
};

const SkuLookupSection = ({
  searchQuery,
  onSearchQueryChange,
  onSearch,
  lookupResult,
  hasSearched,
  isSearching = false,
  embedded = false,
}) => {
  const { textColor, backgroundCardColor, borderColor, secondaryTextColor, mutedBg, successColor } =
    useColors();

  const body = (
    <>
      {!embedded && (
        <>
          <Text color={textColor} fontSize="md" fontWeight="semibold" mb={1}>
            SKU lookup
          </Text>
          <Text color={secondaryTextColor} fontSize="sm" mb={4}>
            Search a canonical SKU to review catalog data, linked listings, and optimizer run history.
          </Text>
        </>
      )}

      {embedded && (
        <Text color={secondaryTextColor} fontSize="sm" mb={4}>
          Search a canonical SKU to review catalog data, linked listings, and optimizer run history.
        </Text>
      )}

      <HStack mb={4} maxW={embedded ? 'full' : 'md'} flexWrap={{ base: 'wrap', md: 'nowrap' }}>
        <InputGroup size="md">
          <InputLeftElement pointerEvents="none">
            <Icon as={FaSearch} color="gray.400" />
          </InputLeftElement>
          <Input
            placeholder="Canonical SKU, e.g. CAR-HO123"
            value={searchQuery}
            onChange={(e) => onSearchQueryChange(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && onSearch()}
            fontFamily="mono"
          />
        </InputGroup>
        <Button colorScheme="teal" onClick={onSearch} isLoading={isSearching}>
          Search
        </Button>
      </HStack>

      {hasSearched && !lookupResult && (
        <Alert status="warning" borderRadius="md">
          <AlertIcon />
          No SKU found for that identifier.
        </Alert>
      )}

      {lookupResult && (
        <VStack align="stretch" spacing={4}>
          <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={3}>
            <Box p={4} bg={mutedBg} borderRadius="md">
              <Text fontSize="xs" color={secondaryTextColor}>
                Canonical SKU
              </Text>
              <Text fontFamily="mono" fontWeight="bold" color={textColor}>
                {lookupResult.canonicalSku}
              </Text>
            </Box>
            <Box p={4} bg={mutedBg} borderRadius="md">
              <Text fontSize="xs" color={secondaryTextColor}>
                Partslink
              </Text>
              <Text fontFamily="mono" fontWeight="semibold" color={textColor}>
                {lookupResult.partslink}
              </Text>
            </Box>
            <Box p={4} bg={mutedBg} borderRadius="md">
              <HStack>
                <Icon as={FaTruck} color="teal.500" />
                <Box>
                  <Text fontSize="xs" color={secondaryTextColor}>
                    Suppliers
                  </Text>
                  <Text fontWeight="bold" color={textColor}>
                    {lookupResult.supplierCount}
                  </Text>
                </Box>
              </HStack>
            </Box>
            <Box p={4} bg={mutedBg} borderRadius="md">
              <HStack>
                <Icon as={FaStore} color="blue.500" />
                <Box>
                  <Text fontSize="xs" color={secondaryTextColor}>
                    Linked listings
                  </Text>
                  <Text fontWeight="bold" color={textColor}>
                    {lookupResult.listingCount}
                  </Text>
                </Box>
              </HStack>
            </Box>
          </SimpleGrid>

          <Box p={4} borderWidth="1px" borderColor={borderColor} borderRadius="md">
            <HStack mb={2}>
              <Icon as={FaBox} color={successColor} />
              <Text fontWeight="semibold" color={textColor} fontSize="sm">
                Catalog
              </Text>
            </HStack>
            <Text color={textColor} fontSize="sm" mb={1}>
              {lookupResult.title}
            </Text>
            <Badge colorScheme="green" variant="subtle">
              Data profile: {lookupResult.dataProfile?.aggregate || '—'}
            </Badge>
          </Box>

          <Box p={4} borderWidth="1px" borderColor={borderColor} borderRadius="md">
            <Text fontWeight="semibold" color={textColor} fontSize="sm" mb={3}>
              Optimizer history
            </Text>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3} fontSize="sm">
              <HStack justify="space-between">
                <Text color={secondaryTextColor}>Status</Text>
                <Badge
                  colorScheme={optimizerStatusColor(lookupResult.optimizer?.status)}
                >
                  {optimizerStatusLabel(lookupResult.optimizer?.status)}
                </Badge>
              </HStack>
              <HStack justify="space-between">
                <Text color={secondaryTextColor}>Last run</Text>
                <Text color={textColor}>{formatDateTime(lookupResult.optimizer?.lastRunAt)}</Text>
              </HStack>
              <HStack justify="space-between">
                <Text color={secondaryTextColor}>Run result</Text>
                <Text color={textColor}>{lookupResult.optimizer?.lastRunStatus}</Text>
              </HStack>
              <HStack justify="space-between">
                <Text color={secondaryTextColor}>Total runs</Text>
                <Text color={textColor}>{lookupResult.optimizer?.runCount ?? 0}</Text>
              </HStack>
            </SimpleGrid>
            <Divider my={3} />
            <Text fontSize="xs" color={secondaryTextColor} mb={2}>
              Verified fields updated
            </Text>
            <HStack flexWrap="wrap" spacing={2}>
              {(lookupResult.optimizer?.verifiedFields || []).map((field) => (
                <Badge key={field} colorScheme="teal" variant="subtle">
                  {field}
                </Badge>
              ))}
            </HStack>
            {lookupResult.optimizer?.notes && (
              <Text fontSize="sm" color={secondaryTextColor} mt={3} fontStyle="italic">
                {lookupResult.optimizer.notes}
              </Text>
            )}
          </Box>

          <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4}>
            <Box>
              <Text fontWeight="semibold" fontSize="sm" color={textColor} mb={2}>
                Supplier SKUs
              </Text>
              <TableContainer borderWidth="1px" borderColor={borderColor} borderRadius="md">
                <Table size="sm">
                  <Thead bg={mutedBg}>
                    <Tr>
                      <Th>Supplier</Th>
                      <Th>SKU</Th>
                      <Th isNumeric>Qty</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {lookupResult.suppliers?.map((row) => (
                      <Tr key={row.sku}>
                        <Td fontSize="xs">{row.name}</Td>
                        <Td fontFamily="mono" fontSize="xs">
                          {row.sku}
                        </Td>
                        <Td isNumeric fontSize="xs">
                          {row.qty}
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </TableContainer>
            </Box>
            <Box>
              <Text fontWeight="semibold" fontSize="sm" color={textColor} mb={2}>
                Linked listings
              </Text>
              <TableContainer borderWidth="1px" borderColor={borderColor} borderRadius="md">
                <Table size="sm">
                  <Thead bg={mutedBg}>
                    <Tr>
                      <Th>Store</Th>
                      <Th>Item ID</Th>
                      <Th>Status</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {lookupResult.listings?.map((row) => (
                      <Tr key={row.marketplaceId}>
                        <Td fontSize="xs">{row.store}</Td>
                        <Td fontFamily="mono" fontSize="xs">
                          {row.marketplaceId}
                        </Td>
                        <Td>
                          <Badge
                            size="sm"
                            colorScheme={row.status === 'active' ? 'green' : 'gray'}
                          >
                            {row.status}
                          </Badge>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </TableContainer>
            </Box>
          </SimpleGrid>
        </VStack>
      )}
    </>
  );

  if (embedded) {
    return body;
  }

  return (
    <Box
      p={5}
      bg={backgroundCardColor}
      borderRadius="lg"
      borderWidth="1px"
      borderColor={borderColor}
      boxShadow="sm"
    >
      {body}
    </Box>
  );
};

export default SkuLookupSection;
